import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { ReactComponent as ErodeLogoBlack } from "../assets/images/erode-logo-black.svg";
import { ReactComponent as LogOutIcon } from "../assets/icons/log-out-01.svg";
import { getVisibleNavLinks } from "../pages/navConfig";
import { isAdminUser } from "../utils/organizationMembership";
import { useCreatorMode } from "../hooks/useCreatorMode";
import ProfilePictureRounded from "./ProfilePictureRounded";
import { performSignOut } from "../store/userSlice";
import { usePostHog } from "posthog-js/react";

// Slide duration — keep in sync with the `duration-300` class on the panel.
const TRANSITION_MS = 300;

// Bar height (py-3 + a 48px pill) — one number the bar reserves and the menu clears.
export const MOBILE_NAV_BAR_HEIGHT = 72;

// Shared open state — the panel outlives route changes so it can close itself on the way out.
interface MobileNavContextValue {
  isOpen: boolean;
  isMounted: boolean;
  toggle: () => void;
  close: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const MobileNavContext = createContext<MobileNavContextValue | null>(null);

const useMobileNav = () => {
  const ctx = useContext(MobileNavContext);
  if (!ctx) throw new Error("useMobileNav must be used within a MobileNavProvider");
  return ctx;
};

// Wraps the VerticalNav layout; owns open state, mount lifecycle, route-change close, scroll lock, and breakpoint close.
export const MobileNavProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  // Keep the menu mounted through its closing transition before unmounting.
  const [isMounted, setIsMounted] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Mount on open; keep mounted through the close transition before unmounting.
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return;
    }
    const timer = setTimeout(() => setIsMounted(false), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Close whenever the route changes underneath it (nav-link tap or browser back button).
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Lock background scrolling while the menu is open.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Close if the viewport grows past the sm breakpoint, else sm:hidden hides the menu but leaves the scroll lock in place.
  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => {
      if (window.innerWidth >= 640) setIsOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isOpen]);

  const value = useMemo(
    () => ({ isOpen, isMounted, toggle, close, triggerRef }),
    [isOpen, isMounted, toggle, close]
  );

  return <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>;
};

// One control both ways — the bars fold to a cross in place, so no second close button appears.
const MenuGlyph = ({ isOpen }: { isOpen: boolean }) => {
  // top-[7px] rather than a centring translate, so the spread is the only transform in play
  const bar =
    "absolute left-0 top-[7px] block h-0.5 w-full rounded-full bg-current transition duration-300 ease-in-out";
  return (
    <span aria-hidden className="relative block h-4 w-5">
      <span className={`${bar} ${isOpen ? "translate-y-0 rotate-45" : "-translate-y-[6px]"}`} />
      <span className={`${bar} ${isOpen ? "opacity-0" : "opacity-100"}`} />
      <span className={`${bar} ${isOpen ? "translate-y-0 -rotate-45" : "translate-y-[6px]"}`} />
    </span>
  );
};

// Mobile top bar — the site's floating nav pill at app density; renders nothing off-provider.
export const MobileNavBar = () => {
  const ctx = useContext(MobileNavContext);
  const navigate = useNavigate();
  if (!ctx) return null;
  const { isOpen, isMounted, toggle, triggerRef } = ctx;

  return (
    <>
      {/* Spacer: the bar is fixed, so this holds its height in the column */}
      <div aria-hidden className="flex-shrink-0 sm:hidden" style={{ height: MOBILE_NAV_BAR_HEIGHT }} />
      {/* Fixed above the panel, so the bar stays put and its trigger is the one control that closes the menu */}
      <div
        className="eai-gutter-x fixed inset-x-0 top-0 z-[1001] flex items-center py-3 sm:hidden"
        style={{ height: MOBILE_NAV_BAR_HEIGHT }}
      >
        {/* eai-gutter-x puts the pill's edges on the page column, pl-4/pr-2.5 its contents on the phone card line (eai-panel p-4) */}
        {/* Open, the frame dissolves onto the wash; the border stays transparent so nothing shifts */}
        <div
          className={`flex h-12 w-full items-center justify-between rounded-eai-card border pl-4 pr-2.5 transition duration-300 ${
            isOpen
              ? "border-transparent bg-transparent"
              : "border-eai-border bg-eai-surface-card/80 shadow-eai-card backdrop-blur-xl"
          }`}
        >
          <button
            type="button"
            onClick={() => navigate("/home")}
            title="Home"
            className="flex items-center"
          >
            <ErodeLogoBlack className="h-6 w-auto" />
          </button>
          <button
            ref={triggerRef}
            type="button"
            onClick={toggle}
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
            aria-controls={isMounted ? "mobile-nav-menu" : undefined}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-eai-ink-muted transition-colors active:bg-eai-surface-active"
          >
            <MenuGlyph isOpen={isOpen} />
          </button>
        </div>
      </div>
    </>
  );
};

// The menu — full-bleed under the bar; rendered once from VerticalNav so it survives navigation.
export const MobileNavPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const user = useSelector((state: RootState) => state.user);
  const { isOpen, isMounted, close, triggerRef } = useMobileNav();
  const [signingOut, setSigningOut] = useState(false);

  // Drives the slide; toggled after mount so the motion animates.
  const [isShown, setIsShown] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Start the slide-out as soon as the menu is closing.
  useEffect(() => {
    if (!isOpen) setIsShown(false);
  }, [isOpen]);

  // Escape closes; Tab is held to the trigger + menu so the covered page stays unreachable.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = [
        triggerRef.current,
        ...Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ),
      ].filter(Boolean) as HTMLElement[];
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inScope = !!active && focusables.includes(active);
      if (e.shiftKey) {
        if (active === first || !inScope) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !inScope) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close, triggerRef]);

  // Read offsetHeight first so -translate-y-full commits and the slide fires on every open.
  useLayoutEffect(() => {
    if (isOpen && isMounted && panelRef.current) {
      void panelRef.current.offsetHeight;
      setIsShown(true);
    }
  }, [isOpen, isMounted]);

  const groups = (user?.details as { groups?: string[] })?.groups;
  const isAdmin = isAdminUser(groups);
  const permissionDetails = user?.permissionDetails as {
    profilePictureUrl?: string;
    name?: string;
  };
  const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS === "true";
  const { canCreate } = useCreatorMode();

  // Same items, ordering, and gating as the VerticalNav sidebar — shared config.
  const navLinks = isMounted ? getVisibleNavLinks(isAdmin, subscriptionsEnabled, canCreate) : [];

  // Same double-tap guard as the rail's — the reload tears this down anyway.
  const handleSignOut = async () => {
    if (signingOut) return;
    try {
      setSigningOut(true);
      await performSignOut({ posthog });
    } catch (error) {
      console.error("Error signing out", error);
      setSigningOut(false);
    }
  };

  // Navigate and close together; the menu slides away while the new page loads underneath it.
  const handleNavigate = (path: string) => {
    navigate(path);
    close();
  };

  if (!isMounted) return null;

  return (
    <div
      ref={panelRef}
      id="mobile-nav-menu"
      // Full-bleed so the wash runs behind the bar too — stopping at it left a seam at the grid
      className={`eai-menu-bg fixed inset-0 z-[1000] flex flex-col transition-transform duration-300 ease-in-out sm:hidden ${
        isShown ? "translate-y-0" : "-translate-y-full"
      }`}
      style={{ paddingTop: MOBILE_NAV_BAR_HEIGHT }}
    >
      {/* eai-gutter-x gives the row pills the bar pill's edges; px-4 puts each icon on the mark's line */}
      <nav aria-label="Main" className="eai-gutter-x flex flex-col gap-0.5 overflow-y-auto py-3">
        {navLinks.map((link) => {
          const isActive = link.isActive(location.pathname);
          return (
            <button
              key={link.path}
              type="button"
              onClick={() => handleNavigate(link.path)}
              aria-current={isActive ? "page" : undefined}
              // py-3 over the rail's py-2 — 44px rows, the phone tap target
              className={`eai-nav-item px-4 py-3 ${isActive ? "eai-nav-item-active" : ""}`}
            >
              <div className={`eai-nav-icon ${isActive ? "eai-nav-icon-active" : ""}`}>
                <link.Icon className={`h-4 w-4 ${link.mobileIconClassName || ""}`} />
              </div>
              <span className="eai-nav-label">{link.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Profile + sign out pinned to the bottom, mirroring the rail's footer */}
      <div className="mt-auto flex items-center border-t border-eai-border">
      <button
        type="button"
        onClick={() => handleNavigate("/home/settings")}
        title={permissionDetails?.name || "User"}
        className="flex min-w-0 flex-1 items-center gap-3 px-7 py-4 text-left transition-colors active:bg-eai-surface-active"
      >
        <ProfilePictureRounded
          pictureURL={permissionDetails?.profilePictureUrl}
          diameter={36}
          className="flex-shrink-0"
          borderClassName="border border-eai-border"
          alt={permissionDetails?.name || "User"}
        />
        <span className="flex min-w-0 flex-col items-start">
          <span className="max-w-[200px] truncate font-outfit text-sm font-medium text-eai-ink-soft">
            {permissionDetails?.name || "User"}
          </span>
          <span className="font-outfit text-xs text-eai-ink-faint">
            {isAdmin ? "Admin" : "User"}
          </span>
        </span>
      </button>
      {/* Its own 44px target — a destructive action must not share the profile row's hit box */}
      <button
        type="button"
        onClick={handleSignOut}
        disabled={signingOut}
        title="Sign out"
        aria-label="Sign out"
        className="mr-5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-eai-ink-subtle transition-colors active:bg-eai-danger-soft active:text-eai-danger disabled:opacity-40"
      >
        {signingOut ? (
          // Same feedback as the rail and the settings page, at icon size
          <svg className="h-[18px] w-[18px] animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <LogOutIcon aria-hidden className="h-[18px] w-[18px]" />
        )}
      </button>
      </div>
    </div>
  );
};
